import { useEffect, useMemo, useState } from "react";
import ModalComponent from "../../components/modals";
import ShowTable from "../../components/tables/showTable";
import LaddaButton from "../../components/buttons/laddaButton";
import { callApiAsync, getTaskDaysTable } from "../../services/apiService";
import { formatAmPm, formatCustomDate, formatDateOnly, formatWithoutHour } from "../../services/dateService";

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return "—";
  try {
    const date = new Date(dateTimeString);
    const dateStr = date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
    const timeStr = date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit"
    });
    return `${dateStr} ${timeStr}`;
  } catch (e) {
    return String(dateTimeString).slice(0, 19).replace("T", " ");
  }
};

const pad2 = (n) => String(n).padStart(2, "0");

const toMysqlDate = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};

const safeText = (v) => (v == null || v === "" ? "—" : v);

const TaskDayCommentsModal = ({
  open,
  onClose,
  task,
  defaultDate = new Date()
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taskInfo, setTaskInfo] = useState(null);

  const taskId = task?.id ?? null;

  const loadData = async () => {

    if (!taskId) return;
    const dayStr = toMysqlDate(defaultDate);
    setLoading(true);

    await callApiAsync(
      () => getTaskDaysTable(taskId, dayStr),
      (resp) => {
        const taskData = resp?.task ?? null;
        const list = Array.isArray(resp?.days) ? resp.days : resp?.items || [];

        setTaskInfo(taskData);

        let mapped = list.map((r, idx) => ({
          id: r.id ?? idx,
          day: r.day ?? `${dayStr}`,
          comment: r.complete_comment ?? "",
          start_hour: r.start_hour ?? null,
          end_hour: r.end_hour ?? null,
          completed_time: r.completed_time ?? null
        }));

        if (
          (!taskData?.is_continue || taskData?.is_continue === 0) &&
          mapped.length === 0
        ) {
          const fallbackDate =
            taskData?.end_date ||
            taskData?.initial_date ||
            `${dayStr} 00:00:00`;
          mapped = [
            {
              id: "single-0",
              day: fallbackDate,
              comment: "",
              start_hour: null,
              end_hour: null,
              completed_time: null
            }
          ];
        }

        setRows(mapped);
      },
      setLoading
    );
  };

  useEffect(() => {
    if (open) loadData();
  }, [open, taskId]);

  const columns = useMemo(
    () => [
      {
        header: "Fecha",
        field: "day",
        body: (row) => {
          if (!row.day) return "—";
          // Si es string con fecha y hora, extraer solo la fecha
          if (typeof row.day === "string" && row.day.includes(" ")) {
            return row.day.split(" ")[0];
          }
          // Si es string ISO o Date, usar formatDateOnly
          return formatDateOnly(row.day) || "—";
        },
        align: "center",
        headerClassName: "table-header-styled table-header-blueBlack "
      },
      {
        header: "Comentario",
        field: "comment",
        tooltipField: "comment",
        body: (row) => safeText(row.comment),
        width: "350px",
        headerClassName: "table-header-styled table-header-blueBlack"
      },
      {
        header: "Inicio",
        field: "start_hour",
        body: (row) => formatAmPm(row.start_hour),
        align: "center",
        headerClassName: "table-header-styled table-header-blueBlack"
      },
      {
        header: "Fin",
        field: "end_hour",
        body: (row) => formatAmPm(row.end_hour),

        align: "center",
        headerClassName: "table-header-styled table-header-blueBlack"
      },
      {
        header: "Completado",
        field: "completed_time",
        body: (row) => formatAmPm(row.completed_time),

        align: "center",
        headerClassName: "table-header-styled table-header-blueBlack"
      }
    ],
    []
  );

  return (
    <ModalComponent
      open={open}
      handleClose={onClose}
      title={<>Comentarios de la actividad</>}
      actions={
        <div className="HContainer j-between w-complete">
          <div />
          <LaddaButton variant="outlined" onClick={onClose}>
            Cerrar
          </LaddaButton>
        </div>
      }
    >
      <div className="VContainer" style={{ gap: 12 }}>
        {taskInfo && (
          <>

            <div
              style={{
                backgroundColor: "#f9f9ff",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                fontSize: "14px"
              }}
            >
              <p style={{ margin: 0 }}>
                <b>Descripción:</b>{" "}
                {taskInfo?.description ? taskInfo.description : "—"}
                <br />
                <b>Proceso:</b> {taskInfo?.process?.name || "—"}
                <br />
                {taskInfo?.is_continue ? (
                  <>
                    <b>Rango:</b>{" "}
                    {taskInfo?.initial_date
                      ? `${String(taskInfo.initial_date).slice(0, 10)} → ${taskInfo?.end_date
                        ? String(taskInfo.end_date).slice(0, 10)
                        : "—"
                      }`
                      : "—"}
                  </>
                ) : (
                  <>
                    <b>Fecha:</b>{" "}
                    {taskInfo?.end_date
                      ? String(taskInfo.end_date).slice(0, 10)
                      : taskInfo?.initial_date
                        ? String(taskInfo.initial_date).slice(0, 10)
                        : "—"}
                  </>
                )}
                <br />
                <b>Clasificación:</b> {taskInfo?.calification || "—"}{" "}
              </p>
            </div>

            {taskInfo?.deleted_at && (
              <div
                style={{
                  backgroundColor: "#ffebee",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ffcdd2",
                  fontSize: "14px"
                }}
              >
                <p style={{ margin: 0, color: "#c62828" }}>
                  <b>Esta Tarea ha sido eliminada</b>
                  <br />
                  <b>Fecha de eliminación:</b> {formatDateTime(taskInfo.deleted_at)}
                  {taskInfo?.deletion_comment && (
                    <>
                      <br />
                      <b>Comentario de eliminación:</b>{" "}
                      {taskInfo.deletion_comment}
                    </>
                  )}
                </p>
              </div>
            )}
          </>
        )}

        <ShowTable
          data={rows}
          columns={columns}
          pagination={false}
          scrollable
          scrollHeight="400px"
          stripedRows
          compact
          emptyMessage={
            loading ? "Cargando..." : "Sin registros para esta tarea"
          }
          showTooltips
          globalFilterFields={["comment", "day"]}
        />
      </div>
    </ModalComponent>
  );
};

export default TaskDayCommentsModal;